// PUBLIC — /start-company (no auth, no shell). Mobile-first multi-step wizard
// for starting a U.S. company. Mirrors the SelfApplyPage public-route pattern.
//
// Compliance invariants (see i18n/start-company/form.ts): administrative filing
// coordination only; no legal/tax advice; no guaranteed approval/EIN/timeline;
// price shown ONLY from GET /config or prepare-payment. Card data goes straight
// to Stripe (PaymentStep). SSN/ITIN is write-only and only asked for SSN/ITIN
// tax-id types — never for a foreign applicant.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  FileCheck2,
  Lock,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  Plus,
  Trash2,
  Loader2,
} from 'lucide-react'
import { useLocaleStore } from '../../store/locale'
import { useT } from '../../i18n'
import { START_COMPANY_STRINGS } from '../../i18n/start-company/form'
import { formatCurrency } from '../../lib/currency'
import {
  createDraft,
  useDraft,
  usePatchDraft,
  usePublicConfig,
  type DraftUpdate,
  type EntityType,
  type OwnerInput,
  type PublicRegistrationView,
  type TaxIdType,
} from '../../api/company-registration-public'
import {
  CheckboxField,
  RadioGroup,
  SelectField,
  TextArea,
  TextField,
  WizardStepper,
} from './ui'
import {
  deriveNumberOfMembers,
  isOwnershipOverfull,
  isValidEmail,
  maskTaxIdLast4,
  maskTaxIdValue,
  taxIdRequiresIdentifier,
} from './helpers'
import { PaymentStep } from './PaymentStep'

const CONSENT_VERSION = 'v1-2026-07'
const TOTAL_STEPS = 6

// Resolved (single-locale) dictionary shape used by step components.
type Dict = (typeof START_COMPANY_STRINGS)['en']

// ─── Local form model ────────────────────────────────────────────────────────
type EinForm = {
  reason_for_applying: string
  date_business_started: string
  date_tbd: boolean
  closing_month: string
  employees_agricultural: string
  employees_household: string
  employees_other: string
  form_944: boolean | null
  first_wages_date: string
  principal_products: string
  previous_ein_applied: boolean
  previous_ein: string
}

type FormState = {
  contact_full_name: string
  contact_email: string
  contact_phone: string
  preferred_language: string
  country_of_residence: string

  requested_company_name_1: string
  requested_company_name_2: string
  requested_company_name_3: string
  trade_name_dba: string
  entity_type: EntityType | ''
  preferred_state: string
  state_guidance_requested: boolean
  principal_business_category: string
  business_activity_description: string
  desired_effective_date_type: 'asap' | 'future'
  desired_effective_date: string
  mailing_address_status: 'have' | 'need_assistance' | ''
  mailing_address: string
  physical_address: string
  county: string
  registered_agent_status: 'have' | 'need_assistance' | 'not_sure' | ''
  management_type: 'member_managed' | 'manager_managed' | 'not_sure' | ''

  responsible_party_name: string
  responsible_party_title: string
  rp_address: string
  tax_id_type: TaxIdType | ''
  tax_id_value: string
  owners: OwnerInput[]
  number_of_members: string

  ein: EinForm

  third_party_designee_authorized: boolean
  certification_accepted: boolean
}

const EMPTY_EIN: EinForm = {
  reason_for_applying: '',
  date_business_started: '',
  date_tbd: false,
  closing_month: '',
  employees_agricultural: '',
  employees_household: '',
  employees_other: '',
  form_944: null,
  first_wages_date: '',
  principal_products: '',
  previous_ein_applied: false,
  previous_ein: '',
}

const EMPTY_FORM: FormState = {
  contact_full_name: '',
  contact_email: '',
  contact_phone: '',
  preferred_language: '',
  country_of_residence: '',
  requested_company_name_1: '',
  requested_company_name_2: '',
  requested_company_name_3: '',
  trade_name_dba: '',
  entity_type: '',
  preferred_state: '',
  state_guidance_requested: false,
  principal_business_category: '',
  business_activity_description: '',
  desired_effective_date_type: 'asap',
  desired_effective_date: '',
  mailing_address_status: '',
  mailing_address: '',
  physical_address: '',
  county: '',
  registered_agent_status: '',
  management_type: '',
  responsible_party_name: '',
  responsible_party_title: '',
  rp_address: '',
  tax_id_type: '',
  tax_id_value: '',
  owners: [{ full_name: '', address: '', ownership_percentage: null, sort_order: 0 }],
  number_of_members: '1',
  ein: EMPTY_EIN,
  third_party_designee_authorized: false,
  certification_accepted: false,
}

function numOrNull(s: string): number | null {
  if (s.trim() === '') return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

// Map a resumed server view back into the local form model.
function viewToForm(v: PublicRegistrationView): FormState {
  const cd = v.company_details ?? {}
  const rp = v.responsible_party_details ?? {}
  const ein = v.ein_answers ?? {}
  return {
    contact_full_name: v.contact_full_name ?? '',
    contact_email: v.contact_email ?? '',
    contact_phone: v.contact_phone ?? '',
    preferred_language: v.preferred_language ?? '',
    country_of_residence: v.country_of_residence ?? '',
    requested_company_name_1: v.requested_company_name_1 ?? '',
    requested_company_name_2: v.requested_company_name_2 ?? '',
    requested_company_name_3: v.requested_company_name_3 ?? '',
    trade_name_dba: v.trade_name_dba ?? '',
    entity_type: v.entity_type ?? '',
    preferred_state: v.preferred_state ?? '',
    state_guidance_requested: v.state_guidance_requested ?? false,
    principal_business_category: v.principal_business_category ?? '',
    business_activity_description: v.business_activity_description ?? '',
    desired_effective_date_type: v.desired_effective_date_type ?? 'asap',
    desired_effective_date: v.desired_effective_date ?? '',
    mailing_address_status: v.mailing_address_status ?? '',
    mailing_address: (cd.mailing_address as string) ?? '',
    physical_address: (cd.physical_address as string) ?? '',
    county: (cd.county as string) ?? '',
    registered_agent_status: v.registered_agent_status ?? '',
    management_type: v.management_type ?? '',
    responsible_party_name: v.responsible_party_name ?? '',
    responsible_party_title: v.responsible_party_title ?? '',
    rp_address: (rp.address as string) ?? '',
    tax_id_type: v.tax_id_type ?? '',
    tax_id_value: '', // never hydrated — write-only
    owners:
      v.owners && v.owners.length > 0
        ? v.owners
        : [{ full_name: '', address: '', ownership_percentage: null, sort_order: 0 }],
    number_of_members: v.number_of_members != null ? String(v.number_of_members) : '1',
    ein: {
      reason_for_applying: ein.reason_for_applying ?? '',
      date_business_started: ein.date_business_started ?? '',
      date_tbd: false,
      closing_month: ein.closing_month ?? '',
      employees_agricultural:
        ein.employees_agricultural != null ? String(ein.employees_agricultural) : '',
      employees_household:
        ein.employees_household != null ? String(ein.employees_household) : '',
      employees_other: ein.employees_other != null ? String(ein.employees_other) : '',
      form_944: ein.form_944 ?? null,
      first_wages_date: ein.first_wages_date ?? '',
      principal_products: ein.principal_products ?? '',
      previous_ein_applied: ein.previous_ein_applied ?? false,
      previous_ein: ein.previous_ein ?? '',
    },
    third_party_designee_authorized: v.third_party_designee_authorized ?? false,
    certification_accepted: v.certification_accepted ?? false,
  }
}

// Build the DraftUpdate patch from local state. tax_id_value is only sent when
// the user actually typed one (write-only, for SSN/ITIN types).
function buildPatch(form: FormState, locale: string): DraftUpdate {
  const sendTaxValue =
    taxIdRequiresIdentifier(form.tax_id_type || null) && form.tax_id_value.trim() !== ''
  return {
    locale,
    contact_full_name: form.contact_full_name || null,
    contact_email: form.contact_email || null,
    contact_phone: form.contact_phone || null,
    preferred_language: form.preferred_language || null,
    country_of_residence: form.country_of_residence || null,

    requested_company_name_1: form.requested_company_name_1 || null,
    requested_company_name_2: form.requested_company_name_2 || null,
    requested_company_name_3: form.requested_company_name_3 || null,
    trade_name_dba: form.trade_name_dba || null,
    entity_type: form.entity_type || null,
    preferred_state: form.preferred_state || null,
    state_guidance_requested: form.state_guidance_requested,
    principal_business_category: form.principal_business_category || null,
    business_activity_description: form.business_activity_description || null,
    desired_effective_date_type: form.desired_effective_date_type,
    desired_effective_date:
      form.desired_effective_date_type === 'future' && form.desired_effective_date
        ? form.desired_effective_date
        : null,
    mailing_address_status: form.mailing_address_status || null,
    registered_agent_status: form.registered_agent_status || null,
    management_type: form.management_type || null,
    company_details: {
      mailing_address: form.mailing_address || null,
      physical_address: form.physical_address || null,
      county: form.county || null,
    },

    responsible_party_name: form.responsible_party_name || null,
    responsible_party_title: form.responsible_party_title || null,
    responsible_party_details: { address: form.rp_address || null },
    tax_id_type: form.tax_id_type || null,
    ...(sendTaxValue ? { tax_id_value: form.tax_id_value.trim() } : {}),
    owners: form.owners
      .filter((o) => (o.full_name ?? '').trim() !== '')
      .map((o, i) => ({ ...o, sort_order: i })),
    number_of_members: numOrNull(form.number_of_members),

    ein_answers: {
      reason_for_applying: form.ein.reason_for_applying || null,
      date_business_started: form.ein.date_tbd
        ? null
        : form.ein.date_business_started || null,
      closing_month: form.ein.closing_month || null,
      employees_agricultural: numOrNull(form.ein.employees_agricultural),
      employees_household: numOrNull(form.ein.employees_household),
      employees_other: numOrNull(form.ein.employees_other),
      form_944: form.ein.form_944,
      first_wages_date: form.ein.first_wages_date || null,
      principal_products: form.ein.principal_products || null,
      previous_ein_applied: form.ein.previous_ein_applied,
      previous_ein: form.ein.previous_ein_applied ? form.ein.previous_ein || null : null,
    },

    consent_version: CONSENT_VERSION,
    certification_accepted: form.certification_accepted,
    third_party_designee_authorized: form.third_party_designee_authorized,
  }
}

export function StartCompanyPage() {
  const t = useT(START_COMPANY_STRINGS)
  const locale = useLocaleStore((s) => s.locale)
  const setLocale = useLocaleStore((s) => s.setLocale)

  // Default language from the browser on first mount (spec: tr → tr else en).
  const langInitRef = useRef(false)
  useEffect(() => {
    if (langInitRef.current) return
    langInitRef.current = true
    const nav = navigator.language || 'en'
    setLocale(nav.toLowerCase().startsWith('tr') ? 'tr' : 'en')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [phase, setPhase] = useState<'checking' | 'landing' | 'wizard'>('checking')
  const [step, setStep] = useState(0)
  const [humanRef, setHumanRef] = useState<string>('')
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [hydrated, setHydrated] = useState(false)
  const [website, setWebsite] = useState('') // honeypot
  // number_of_members auto-tracks the named-owner count until the user edits the
  // field directly; after that, owner edits no longer clobber their value.
  const [membersAuto, setMembersAuto] = useState(true)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const config = usePublicConfig()
  // The current draft is whatever the HttpOnly resume cookie resolves to — no
  // token in JS. On mount this probes for a resumable draft (404 → none).
  const draft = useDraft(true)
  const patch = usePatchDraft()

  // Boot: once the cookie-draft probe settles, decide landing vs resume
  // (adjust-during-render: the phase check turns itself off after the set).
  if (phase === 'checking') {
    if (draft.isSuccess && draft.data) {
      setForm(viewToForm(draft.data))
      setHumanRef(draft.data.human_reference)
      setHydrated(true)
      setPhase('wizard')
      // Returning from a redirect payment (3DS/bank) → resume on the payment step
      // so PaymentStep re-mounts and its poller runs (PaymentStep strips params).
      const params = new URLSearchParams(window.location.search)
      if (params.get('cr_confirm') === '1' || params.get('redirect_status')) {
        setStep(5)
      }
    } else if (draft.isError) {
      setPhase('landing')
    }
  }

  const patchTag = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const patchEin = <K extends keyof EinForm>(key: K, value: EinForm[K]) =>
    setForm((f) => ({ ...f, ein: { ...f.ein, [key]: value } }))

  async function persist(): Promise<boolean> {
    if (phase !== 'wizard') return true
    try {
      await patch.mutateAsync(buildPatch(form, locale))
      return true
    } catch {
      return false
    }
  }

  async function startApplication() {
    if (website.trim() !== '') return // honeypot tripped — silently block
    setCreating(true)
    setCreateError(null)
    try {
      // Forward the honeypot value so the server has its own bot signal (defense
      // in depth) — the client early-return above only blocks the obvious case.
      // The resume token is set as an HttpOnly cookie by this call (not returned).
      const res = await createDraft({ locale, website })
      setHumanRef(res.human_reference)
      setHydrated(true) // brand-new draft — nothing to hydrate over local EMPTY_FORM
      setPhase('wizard')
      setStep(0)
      // Populate the draft query (used for the review step's masked tax id).
      await draft.refetch()
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : t.common.errorTitle)
    } finally {
      setCreating(false)
    }
  }

  function validateStep(idx: number): boolean {
    const e: Record<string, string> = {}
    if (idx === 0) {
      if (form.contact_full_name.trim() === '') e.contact_full_name = t.contact.nameRequired
      if (!isValidEmail(form.contact_email)) e.contact_email = t.contact.emailInvalid
    }
    if (idx === 1) {
      if (form.requested_company_name_1.trim() === '')
        e.requested_company_name_1 = t.company.name1Required
    }
    if (idx === 2) {
      if (form.responsible_party_name.trim() === '')
        e.responsible_party_name = t.parties.rpNameRequired
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function goNext() {
    if (website.trim() !== '') return // honeypot
    if (!validateStep(step)) return
    await persist()
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function goBack() {
    setErrors({})
    setStep((s) => Math.max(s - 1, 0))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function editSection(idx: number) {
    setStep(idx)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ─── Owners helpers ────────────────────────────────────────────────────────
  // Re-derive members ONLY while the user hasn't taken over the field manually.
  const membersFor = (owners: OwnerInput[], current: string) =>
    membersAuto ? String(deriveNumberOfMembers(owners)) : current
  function updateOwner(i: number, patchO: Partial<OwnerInput>) {
    setForm((f) => {
      const owners = f.owners.map((o, idx) => (idx === i ? { ...o, ...patchO } : o))
      return { ...f, owners, number_of_members: membersFor(owners, f.number_of_members) }
    })
  }
  function addOwner() {
    setForm((f) => {
      const owners = [
        ...f.owners,
        { full_name: '', address: '', ownership_percentage: null, sort_order: f.owners.length },
      ]
      return { ...f, owners, number_of_members: membersFor(owners, f.number_of_members) }
    })
  }
  function removeOwner(i: number) {
    setForm((f) => {
      const owners = f.owners.filter((_, idx) => idx !== i)
      const safe = owners.length > 0 ? owners : EMPTY_FORM.owners
      return { ...f, owners: safe, number_of_members: membersFor(safe, f.number_of_members) }
    })
  }

  const showTaxInput = taxIdRequiresIdentifier(form.tax_id_type || null)
  const ownershipWarn = isOwnershipOverfull(form.owners)

  // ─── Honeypot field (visually hidden, off-screen, not tab-focusable) ────────
  const honeypot = (
    <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
      <label htmlFor="sc-website">Website</label>
      <input
        id="sc-website"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
      />
    </div>
  )

  const langSwitcher = (
    <div className="inline-flex overflow-hidden rounded-md border border-[var(--color-border)] text-xs">
      {(['tr', 'en'] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setLocale(l)}
          aria-pressed={locale === l}
          className={
            'px-3 py-1.5 font-medium transition-colors ' +
            (locale === l
              ? 'bg-[var(--color-action)] text-[var(--color-action-foreground)]'
              : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)]')
          }
        >
          {l === 'tr' ? t.lang.tr : t.lang.en}
        </button>
      ))}
    </div>
  )

  // ─── Booting: probing the resume cookie for an in-progress draft ────────────
  if (phase === 'checking') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" aria-busy="true">
        <Loader2 className="animate-spin text-brand-600" size={28} aria-hidden="true" />
      </div>
    )
  }

  // ─── Landing (hero) ─────────────────────────────────────────────────────────
  if (phase === 'landing') {
    const benefitIcons = [Sparkles, ShieldCheck, FileCheck2]
    return (
      <div className="pex-start-company-landing min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-50 via-brand-50/30 to-white">
        {honeypot}
        <div className="mx-auto flex max-w-3xl flex-col px-4 py-8 sm:px-6 sm:py-14">
          <div className="mb-6 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2 text-[var(--color-text)]">
              <Lock size={18} aria-hidden="true" />
              <span className="text-lg font-semibold tracking-tight">Pexify</span>
            </span>
            {langSwitcher}
          </div>

          <h1 className="text-2xl font-bold leading-tight tracking-tight text-[var(--color-text)] sm:text-3xl">
            {t.hero.title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-muted)] sm:text-base">
            {t.hero.subtitle}
          </p>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            {t.hero.benefits.map((b, i) => {
              const Icon = benefitIcons[i] ?? Sparkles
              return (
                <div
                  key={b.title}
                  className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm"
                >
                  <Icon size={20} className="mb-2 text-[var(--color-signal)]" aria-hidden="true" />
                  <h3 className="text-sm font-semibold text-[var(--color-text)]">{b.title}</h3>
                  <p className="mt-1 text-xs text-[var(--color-text-muted)]">{b.body}</p>
                </div>
              )
            })}
          </div>

          {config.data?.enabled === false ? (
            <div className="mt-8 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              {t.common.errorTitle}
            </div>
          ) : (
            <div className="mt-8">
              <button
                type="button"
                onClick={startApplication}
                disabled={creating}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[var(--color-action)] px-6 py-3 text-sm font-semibold text-[var(--color-action-foreground)] transition-colors hover:bg-[var(--color-action-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:opacity-50 sm:w-auto"
              >
                {creating ? t.common.saving : t.hero.cta}
                <ArrowRight size={16} aria-hidden="true" />
              </button>
              {createError && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-[var(--color-danger)]" role="alert">
                  <AlertCircle size={13} aria-hidden="true" />
                  {createError}
                </p>
              )}
            </div>
          )}

          <p className="mt-8 border-t border-[var(--color-border)] pt-4 text-xs text-[var(--color-text-muted)]">
            {t.hero.disclaimer}
          </p>
        </div>
      </div>
    )
  }

  // ─── Wizard ─────────────────────────────────────────────────────────────────
  const stepLabels = [
    t.steps.contact,
    t.steps.company,
    t.steps.parties,
    t.steps.ein,
    t.steps.review,
    t.steps.payment,
  ]

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[var(--color-surface-muted)]">
      {honeypot}
      <div className="mx-auto flex max-w-2xl flex-col px-4 py-6 sm:px-6 sm:py-10">
        <header className="mb-5 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-[var(--color-text)]">
            <Lock size={16} aria-hidden="true" />
            <span className="text-base font-semibold tracking-tight">Pexify</span>
          </span>
          {langSwitcher}
        </header>

        {draft.data && !hydrated && (
          <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-text-muted)]">
            {t.common.loading}
          </div>
        )}
        {hydrated && draft.data && step === 0 && (
          <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-xs text-[var(--color-text)]">
            {t.hero.resumeNotice}
          </div>
        )}

        <div className="mb-5">
          <WizardStepper steps={stepLabels} current={step} />
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">{t.nav.step(step + 1, TOTAL_STEPS)}</p>
        </div>

        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm sm:p-6">
          {step === 0 && (
            <StepContact form={form} setField={patchTag} t={t} errors={errors} />
          )}
          {step === 1 && (
            <StepCompany form={form} setField={patchTag} t={t} errors={errors} />
          )}
          {step === 2 && (
            <StepParties
              form={form}
              setField={patchTag}
              t={t}
              errors={errors}
              showTaxInput={showTaxInput}
              ownershipWarn={ownershipWarn}
              updateOwner={updateOwner}
              addOwner={addOwner}
              removeOwner={removeOwner}
              onMembersEdit={() => setMembersAuto(false)}
            />
          )}
          {step === 3 && <StepEin form={form} setEin={patchEin} setField={patchTag} t={t} />}
          {step === 4 && (
            <StepReview
              form={form}
              t={t}
              config={config.data}
              onEdit={editSection}
              setField={patchTag}
              serverTaxLast4={draft.data?.tax_id_last4}
            />
          )}
          {step === 5 && <PaymentStep humanReference={humanRef} />}
        </div>

        {/* Nav (hidden on payment step — PaymentStep owns its own actions) */}
        {step < 5 && (
          <div className="mt-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={goBack}
              disabled={step === 0}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-4 py-2.5 text-sm font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:opacity-40"
            >
              <ArrowLeft size={15} aria-hidden="true" />
              {t.nav.back}
            </button>

            {step < 4 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={patch.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-action)] px-5 py-2.5 text-sm font-semibold text-[var(--color-action-foreground)] transition-colors hover:bg-[var(--color-action-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:opacity-50"
              >
                {patch.isPending ? t.common.saving : t.nav.next}
                <ArrowRight size={15} aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                onClick={async () => {
                  if (!form.certification_accepted) {
                    setErrors({ certification: t.review.mustCertify })
                    return
                  }
                  setErrors({})
                  await persist()
                  setStep(5)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                disabled={patch.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-action)] px-5 py-2.5 text-sm font-semibold text-[var(--color-action-foreground)] transition-colors hover:bg-[var(--color-action-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:opacity-50"
              >
                {t.review.continueToPayment}
                <ArrowRight size={15} aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-[var(--color-text-muted)]">{t.hero.disclaimer}</p>
      </div>
    </div>
  )
}

// ─── Step components ─────────────────────────────────────────────────────────
type StepProps = {
  form: FormState
  setField: <K extends keyof FormState>(k: K, v: FormState[K]) => void
  t: Dict
  errors: Record<string, string>
}

function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="mb-4 text-lg font-semibold text-[var(--color-text)]">{children}</h2>
}

function StepContact({ form, setField, t, errors }: StepProps) {
  return (
    <div className="space-y-4">
      <SectionHeading>{t.contact.heading}</SectionHeading>
      <TextField
        label={t.contact.fullName}
        value={form.contact_full_name}
        onChange={(v) => setField('contact_full_name', v)}
        required
        autoComplete="name"
        error={errors.contact_full_name}
      />
      <TextField
        label={t.contact.email}
        value={form.contact_email}
        onChange={(v) => setField('contact_email', v)}
        required
        type="email"
        autoComplete="email"
        error={errors.contact_email}
      />
      <TextField
        label={t.contact.phone}
        value={form.contact_phone}
        onChange={(v) => setField('contact_phone', v)}
        type="tel"
        autoComplete="tel"
        inputMode="tel"
      />
      <TextField
        label={t.contact.preferredLanguage}
        value={form.preferred_language}
        onChange={(v) => setField('preferred_language', v)}
      />
      <TextField
        label={t.contact.country}
        value={form.country_of_residence}
        onChange={(v) => setField('country_of_residence', v)}
        autoComplete="country-name"
      />
    </div>
  )
}

function StepCompany({ form, setField, t, errors }: StepProps) {
  return (
    <div className="space-y-4">
      <SectionHeading>{t.company.heading}</SectionHeading>

      <div className="space-y-2">
        <p className="text-sm font-medium text-[var(--color-text)]">{t.company.nameChoicesLabel}</p>
        <p className="text-xs text-[var(--color-text-muted)]">{t.company.nameChoicesHelp}</p>
        <TextField
          label={t.company.name1}
          value={form.requested_company_name_1}
          onChange={(v) => setField('requested_company_name_1', v)}
          required
          error={errors.requested_company_name_1}
        />
        <TextField
          label={t.company.name2}
          value={form.requested_company_name_2}
          onChange={(v) => setField('requested_company_name_2', v)}
          hint={t.common.optional}
        />
        <TextField
          label={t.company.name3}
          value={form.requested_company_name_3}
          onChange={(v) => setField('requested_company_name_3', v)}
          hint={t.common.optional}
        />
      </div>

      <TextField
        label={t.company.dba}
        value={form.trade_name_dba}
        onChange={(v) => setField('trade_name_dba', v)}
        hint={t.common.optional}
      />

      <RadioGroup
        label={t.company.entityType}
        name="entity_type"
        value={form.entity_type}
        onChange={(v) => setField('entity_type', v as EntityType)}
        options={[
          { value: 'single_member_llc', label: t.company.entityOptions.single_member_llc },
          { value: 'multi_member_llc', label: t.company.entityOptions.multi_member_llc },
          { value: 'c_corp', label: t.company.entityOptions.c_corp },
          { value: 'other_unsure', label: t.company.entityOptions.other_unsure },
        ]}
      />

      <TextField
        label={t.company.preferredState}
        value={form.preferred_state}
        onChange={(v) => setField('preferred_state', v)}
      />
      <CheckboxField
        label={t.company.stateGuidance}
        checked={form.state_guidance_requested}
        onChange={(v) => setField('state_guidance_requested', v)}
      />

      <TextField
        label={t.company.category}
        value={form.principal_business_category}
        onChange={(v) => setField('principal_business_category', v)}
      />
      <TextArea
        label={t.company.activityDescription}
        value={form.business_activity_description}
        onChange={(v) => setField('business_activity_description', v)}
        hint={t.common.optional}
        placeholder={t.company.activityHelp}
      />

      <RadioGroup
        label={t.company.effectiveDate}
        name="effective_date_type"
        value={form.desired_effective_date_type}
        onChange={(v) => setField('desired_effective_date_type', v as 'asap' | 'future')}
        options={[
          { value: 'asap', label: t.company.effectiveAsap },
          { value: 'future', label: t.company.effectiveFuture },
        ]}
      />
      {form.desired_effective_date_type === 'future' && (
        <TextField
          label={t.company.effectiveDateField}
          value={form.desired_effective_date}
          onChange={(v) => setField('desired_effective_date', v)}
          type="date"
        />
      )}

      <RadioGroup
        label={t.company.mailingStatus}
        name="mailing_status"
        value={form.mailing_address_status}
        onChange={(v) => setField('mailing_address_status', v as 'have' | 'need_assistance')}
        options={[
          { value: 'have', label: t.company.mailingHave },
          { value: 'need_assistance', label: t.company.mailingNeed },
        ]}
      />
      {form.mailing_address_status === 'have' && (
        <TextField
          label={t.company.mailingAddress}
          value={form.mailing_address}
          onChange={(v) => setField('mailing_address', v)}
        />
      )}

      <TextField
        label={t.company.physicalAddress}
        value={form.physical_address}
        onChange={(v) => setField('physical_address', v)}
      />
      <TextField
        label={t.company.county}
        value={form.county}
        onChange={(v) => setField('county', v)}
      />

      <RadioGroup
        label={t.company.registeredAgent}
        name="registered_agent"
        value={form.registered_agent_status}
        onChange={(v) =>
          setField('registered_agent_status', v as 'have' | 'need_assistance' | 'not_sure')
        }
        options={[
          { value: 'have', label: t.company.agentHave },
          { value: 'need_assistance', label: t.company.agentNeed },
          { value: 'not_sure', label: t.company.agentNotSure },
        ]}
      />

      <RadioGroup
        label={t.company.management}
        name="management_type"
        value={form.management_type}
        onChange={(v) =>
          setField('management_type', v as 'member_managed' | 'manager_managed' | 'not_sure')
        }
        options={[
          { value: 'member_managed', label: t.company.memberManaged },
          { value: 'manager_managed', label: t.company.managerManaged },
          { value: 'not_sure', label: t.company.agentNotSure },
        ]}
      />
    </div>
  )
}

function StepParties({
  form,
  setField,
  t,
  errors,
  showTaxInput,
  ownershipWarn,
  updateOwner,
  addOwner,
  removeOwner,
  onMembersEdit,
}: StepProps & {
  showTaxInput: boolean
  ownershipWarn: boolean
  updateOwner: (i: number, p: Partial<OwnerInput>) => void
  addOwner: () => void
  removeOwner: (i: number) => void
  onMembersEdit: () => void
}) {
  return (
    <div className="space-y-4">
      <SectionHeading>{t.parties.heading}</SectionHeading>

      <TextField
        label={t.parties.rpName}
        value={form.responsible_party_name}
        onChange={(v) => setField('responsible_party_name', v)}
        required
        error={errors.responsible_party_name}
      />
      <TextField
        label={t.parties.rpTitle}
        value={form.responsible_party_title}
        onChange={(v) => setField('responsible_party_title', v)}
        hint={t.parties.rpTitleHelp}
      />
      <TextField
        label={t.parties.rpAddress}
        value={form.rp_address}
        onChange={(v) => setField('rp_address', v)}
      />

      <RadioGroup
        label={t.parties.taxIdType}
        name="tax_id_type"
        value={form.tax_id_type}
        onChange={(v) => setField('tax_id_type', v as TaxIdType)}
        options={[
          { value: 'ssn', label: t.parties.taxSsn },
          { value: 'itin', label: t.parties.taxItin },
          { value: 'foreign_none', label: t.parties.taxForeign },
        ]}
      />
      {showTaxInput ? (
        <TextField
          label={t.parties.taxIdValue}
          value={form.tax_id_value}
          onChange={(v) => setField('tax_id_value', v)}
          hint={t.parties.taxIdHelp}
          inputMode="numeric"
          autoComplete="off"
        />
      ) : form.tax_id_type === 'foreign_none' ? (
        <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-xs text-[var(--color-text-muted)]">
          {t.parties.foreignNote}
        </p>
      ) : null}

      <div className="border-t border-[var(--color-border)] pt-4">
        <p className="text-sm font-medium text-[var(--color-text)]">{t.parties.ownersHeading}</p>
        <p className="mb-3 text-xs text-[var(--color-text-muted)]">{t.parties.ownersHelp}</p>

        <div className="space-y-3">
          {form.owners.map((o, i) => (
            <div key={i} className="rounded-md border border-[var(--color-border)] p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-[var(--color-text-muted)]">#{i + 1}</span>
                {form.owners.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeOwner(i)}
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-danger)] hover:underline"
                  >
                    <Trash2 size={12} aria-hidden="true" />
                    {t.common.remove}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                <TextField
                  label={t.parties.ownerName}
                  value={o.full_name ?? ''}
                  onChange={(v) => updateOwner(i, { full_name: v })}
                />
                <TextField
                  label={t.parties.ownerAddress}
                  value={o.address ?? ''}
                  onChange={(v) => updateOwner(i, { address: v })}
                  hint={t.common.optional}
                />
                <TextField
                  label={t.parties.ownerPct}
                  value={
                    o.ownership_percentage != null ? String(o.ownership_percentage) : ''
                  }
                  onChange={(v) =>
                    updateOwner(i, {
                      ownership_percentage: v.trim() === '' ? null : Number(v),
                    })
                  }
                  type="number"
                  inputMode="decimal"
                  hint={t.common.optional}
                />
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addOwner}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
        >
          <Plus size={14} aria-hidden="true" />
          {t.parties.addOwner}
        </button>

        {ownershipWarn && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700" role="alert">
            <AlertCircle size={13} aria-hidden="true" />
            {t.parties.ownershipOver100}
          </p>
        )}

        <div className="mt-4">
          <TextField
            label={t.parties.numberOfMembers}
            value={form.number_of_members}
            onChange={(v) => {
              onMembersEdit()
              setField('number_of_members', v)
            }}
            type="number"
            inputMode="numeric"
          />
        </div>
      </div>
    </div>
  )
}

function StepEin({
  form,
  setEin,
  setField,
  t,
}: {
  form: FormState
  setEin: <K extends keyof EinForm>(k: K, v: EinForm[K]) => void
  setField: <K extends keyof FormState>(k: K, v: FormState[K]) => void
  t: Dict
}) {
  return (
    <div className="space-y-4">
      <SectionHeading>{t.ein.heading}</SectionHeading>
      <p className="text-xs text-[var(--color-text-muted)]">{t.ein.intro}</p>

      <SelectField
        label={t.ein.reason}
        value={form.ein.reason_for_applying}
        onChange={(v) => setEin('reason_for_applying', v)}
        placeholder="—"
        options={[
          { value: 'started_new_business', label: t.ein.reasonOptions.started_new_business },
          { value: 'hired_employees', label: t.ein.reasonOptions.hired_employees },
          { value: 'banking', label: t.ein.reasonOptions.banking },
          { value: 'changed_org_type', label: t.ein.reasonOptions.changed_org_type },
          { value: 'purchased_business', label: t.ein.reasonOptions.purchased_business },
          { value: 'other', label: t.ein.reasonOptions.other },
        ]}
      />

      <div>
        <TextField
          label={t.ein.dateStarted}
          value={form.ein.date_business_started}
          onChange={(v) => setEin('date_business_started', v)}
          type="date"
        />
        <div className="mt-2">
          <CheckboxField
            label={t.ein.dateTbd}
            checked={form.ein.date_tbd}
            onChange={(v) => setEin('date_tbd', v)}
          />
        </div>
      </div>

      <SelectField
        label={t.ein.closingMonth}
        value={form.ein.closing_month}
        onChange={(v) => setEin('closing_month', v)}
        placeholder="—"
        options={t.months.map((m) => ({ value: m, label: m }))}
      />

      <div>
        <p className="text-sm font-medium text-[var(--color-text)]">{t.ein.employeesHeading}</p>
        <p className="mb-2 text-xs text-[var(--color-text-muted)]">{t.ein.employeesHelp}</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <TextField
            label={t.ein.empAgricultural}
            value={form.ein.employees_agricultural}
            onChange={(v) => setEin('employees_agricultural', v)}
            type="number"
            inputMode="numeric"
          />
          <TextField
            label={t.ein.empHousehold}
            value={form.ein.employees_household}
            onChange={(v) => setEin('employees_household', v)}
            type="number"
            inputMode="numeric"
          />
          <TextField
            label={t.ein.empOther}
            value={form.ein.employees_other}
            onChange={(v) => setEin('employees_other', v)}
            type="number"
            inputMode="numeric"
          />
        </div>
      </div>

      <RadioGroup
        label={t.ein.form944}
        name="form_944"
        value={form.ein.form_944 === null ? '' : form.ein.form_944 ? 'yes' : 'no'}
        onChange={(v) => setEin('form_944', v === 'yes')}
        options={[
          { value: 'yes', label: t.common.yes },
          { value: 'no', label: t.common.no },
        ]}
      />
      <p className="-mt-2 text-xs text-[var(--color-text-muted)]">{t.ein.form944Help}</p>

      <TextField
        label={t.ein.firstWages}
        value={form.ein.first_wages_date}
        onChange={(v) => setEin('first_wages_date', v)}
        type="date"
        hint={t.common.optional}
      />

      <TextArea
        label={t.ein.principalProducts}
        value={form.ein.principal_products}
        onChange={(v) => setEin('principal_products', v)}
        rows={2}
        hint={t.common.optional}
      />

      <RadioGroup
        label={t.ein.previousEin}
        name="previous_ein_applied"
        value={form.ein.previous_ein_applied ? 'yes' : 'no'}
        onChange={(v) => setEin('previous_ein_applied', v === 'yes')}
        options={[
          { value: 'no', label: t.common.no },
          { value: 'yes', label: t.common.yes },
        ]}
      />
      {form.ein.previous_ein_applied && (
        <TextField
          label={t.ein.previousEinNumber}
          value={form.ein.previous_ein}
          onChange={(v) => setEin('previous_ein', v)}
        />
      )}

      <div className="space-y-3 border-t border-[var(--color-border)] pt-4">
        <CheckboxField
          label={t.ein.thirdParty}
          hint={t.ein.thirdPartyHelp}
          checked={form.third_party_designee_authorized}
          onChange={(v) => setField('third_party_designee_authorized', v)}
        />
        <CheckboxField
          label={t.ein.certification}
          hint={t.ein.certificationHelp}
          checked={form.certification_accepted}
          onChange={(v) => setField('certification_accepted', v)}
        />
      </div>
    </div>
  )
}

function ReviewRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-[var(--color-text-muted)]">{label}</span>
      <span className="text-right font-medium text-[var(--color-text)]">{value || '—'}</span>
    </div>
  )
}

// Module-level (react-hooks/static-components): defined inside StepReview it
// was recreated every render, remounting all four review sections.
function ReviewSection({
  title,
  idx,
  children,
  onEdit,
  editLabel,
}: {
  title: string
  idx: number
  children: ReactNode
  onEdit: (idx: number) => void
  editLabel: string
}) {
  return (
    <div className="rounded-md border border-[var(--color-border)] p-3">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text)]">{title}</h3>
        <button
          type="button"
          onClick={() => onEdit(idx)}
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          {editLabel}
        </button>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  )
}

function StepReview({
  form,
  t,
  config,
  onEdit,
  setField,
  serverTaxLast4,
}: {
  form: FormState
  t: Dict
  config: ReturnType<typeof usePublicConfig>['data']
  onEdit: (idx: number) => void
  setField: <K extends keyof FormState>(k: K, v: FormState[K]) => void
  // Masked last-4 from the resumed server view; tax_id_value is never rehydrated
  // (write-only), so on a resumed draft this is the only masked value available.
  serverTaxLast4: string | null | undefined
}) {
  const taxDisplay =
    form.tax_id_type === 'foreign_none'
      ? t.parties.taxForeign
      : form.tax_id_value
        ? maskTaxIdValue(form.tax_id_value)
        : maskTaxIdLast4(serverTaxLast4)

  const entityLabel = form.entity_type ? t.company.entityOptions[form.entity_type] : '—'

  return (
    <div className="space-y-4">
      <SectionHeading>{t.review.heading}</SectionHeading>
      <p className="text-sm text-[var(--color-text-muted)]">{t.review.intro}</p>

      <ReviewSection title={t.review.sectionContact} idx={0} onEdit={onEdit} editLabel={t.review.editSection}>
        <ReviewRow label={t.contact.fullName} value={form.contact_full_name} />
        <ReviewRow label={t.contact.email} value={form.contact_email} />
        <ReviewRow label={t.contact.phone} value={form.contact_phone} />
        <ReviewRow label={t.contact.country} value={form.country_of_residence} />
      </ReviewSection>

      <ReviewSection title={t.review.sectionCompany} idx={1} onEdit={onEdit} editLabel={t.review.editSection}>
        <ReviewRow label={t.company.name1} value={form.requested_company_name_1} />
        <ReviewRow label={t.company.name2} value={form.requested_company_name_2} />
        <ReviewRow label={t.company.name3} value={form.requested_company_name_3} />
        <ReviewRow label={t.company.dba} value={form.trade_name_dba} />
        <ReviewRow label={t.company.entityType} value={entityLabel} />
        <ReviewRow
          label={t.company.preferredState}
          value={
            form.state_guidance_requested
              ? t.company.stateGuidance
              : form.preferred_state
          }
        />
        <ReviewRow label={t.company.category} value={form.principal_business_category} />
      </ReviewSection>

      <ReviewSection title={t.review.sectionParties} idx={2} onEdit={onEdit} editLabel={t.review.editSection}>
        <ReviewRow label={t.parties.rpName} value={form.responsible_party_name} />
        <ReviewRow label={t.parties.rpTitle} value={form.responsible_party_title} />
        <ReviewRow label={t.parties.taxIdType} value={taxDisplay} />
        <ReviewRow
          label={t.parties.ownersHeading}
          value={String(
            form.owners.filter((o) => (o.full_name ?? '').trim() !== '').length,
          )}
        />
        <ReviewRow label={t.parties.numberOfMembers} value={form.number_of_members} />
      </ReviewSection>

      <ReviewSection title={t.review.sectionEin} idx={3} onEdit={onEdit} editLabel={t.review.editSection}>
        <ReviewRow
          label={t.ein.reason}
          value={
            form.ein.reason_for_applying
              ? t.ein.reasonOptions[
                  form.ein.reason_for_applying as keyof typeof t.ein.reasonOptions
                ]
              : '—'
          }
        />
        <ReviewRow
          label={t.ein.dateStarted}
          value={form.ein.date_tbd ? t.ein.dateTbd : form.ein.date_business_started}
        />
        <ReviewRow label={t.ein.principalProducts} value={form.ein.principal_products} />
      </ReviewSection>

      {/* Included / not included + price — all copy from config, never invented. */}
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4">
        {config?.included && config.included.length > 0 && (
          <>
            <p className="text-sm font-semibold text-[var(--color-text)]">{t.review.included}</p>
            <ul className="mt-1.5 space-y-1">
              {config.included.map((inc) => (
                <li key={inc} className="flex items-start gap-2 text-sm text-[var(--color-text)]">
                  <CheckCircle2
                    size={14}
                    className="mt-0.5 flex-shrink-0 text-emerald-600"
                    aria-hidden="true"
                  />
                  {inc}
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="mt-3 text-sm font-semibold text-[var(--color-text)]">{t.review.notIncluded}</p>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">{t.review.notIncludedBody}</p>

        <div className="mt-3 flex items-center justify-between border-t border-[var(--color-border)] pt-3">
          <span className="text-sm font-medium text-[var(--color-text)]">{t.review.amountDue}</span>
          <span className="font-mono text-base font-semibold text-[var(--color-text)]">
            {config?.price_cents != null
              ? formatCurrency(config.price_cents / 100, config.currency || 'USD')
              : t.review.priceUnavailable}
          </span>
        </div>
      </div>

      <div className="space-y-3 rounded-md border border-brand-200 bg-brand-50/50 p-4">
        <CheckboxField
          label={t.ein.certification}
          checked={form.certification_accepted}
          onChange={(v) => setField('certification_accepted', v)}
        />
        <CheckboxField
          label={t.ein.thirdParty}
          checked={form.third_party_designee_authorized}
          onChange={(v) => setField('third_party_designee_authorized', v)}
        />
      </div>
    </div>
  )
}
